import React, { useState, useEffect, useCallback } from 'react';
import authedFetch from '../../lib/authedFetch';
import { useAuth } from '../../context/AuthContext';
import { cn } from '../../lib/utils';
import { useDeepLinkOrg } from '../../hooks/useDeepLinkOrg';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { TileInfoButton } from '../../components/common/TileInfoButton';
import { useQuarterDelta } from '../../hooks/useQuarterDelta';
import { Info, TrendingUp, TrendingDown, Minus, Activity, Brain, Heart, Shield, BookOpen, ChevronRight, AlertTriangle, Bell, X, Settings2, RefreshCw, Upload, FileUp, Download, ClipboardCheck } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Legend, BarChart, Bar, Cell, Tooltip as RechartsTooltip } from 'recharts';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import secureStorage from '../../lib/secureStorage';
import logger from '../../lib/logger';
import api from '../../lib/api';
import { Users } from 'lucide-react';
import { PageShell } from '../../components/layout/PageShell';
import { PageHero } from '../../components/layout/PageHero';
import ChainPositionStrip from '../../components/layout/ChainPositionStrip';
import { getLightSectionTheme, getSectionTheme } from '../../lib/sectionThemes';
import { useTheme } from '../../context/ThemeContext';
import PageSubNav from '../../components/layout/PageSubNav';
import PeLeanOverview from '../../components/people-engine/PeLeanOverview';
import PeTrendsFull from '../../components/people-engine/PeTrendsFull';
import PeCohortComparison from '../../components/people-engine/PeCohortComparison';
import CapabilityDataButton from '../../components/people-engine/CapabilityDataButton';

const PE_SUBNAV = [
  { id: 'overview', label: 'Overview', to: '/people-engine', end: true },
  { id: 'deep-dives', label: 'Priorities', to: '/people-engine/deep-dives' },
  { id: 'trends', label: 'Trends', to: '/people-engine/trends' },
  { id: 'inventory', label: 'Inventory', to: '/people-engine/inventory' },
  { id: 'coverage', label: 'Coverage', to: '/people-engine/coverage' },
];
import CapabilityInventoryPage from '../CapabilityInventoryPage';
import EngineWeakLinkCallout from '../../components/engines/EngineWeakLinkCallout';
import EngineActionPlanCard from '../../components/engines/EngineActionPlanCard';
import CollapsibleSection from '../../components/layout/CollapsibleSection';
import PageToolbar from '../../components/layout/PageToolbar';
import FilterSummaryChip from '../../components/layout/FilterSummaryChip';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Static chart margins hoisted to module scope — prevents re-renders
// caused by inline object literal identity changes.
const LINE_CHART_MARGIN = { top: 5, right: 20, bottom: 5, left: 0 };
const BAR_CHART_COHORT_MARGIN = { top: 5, right: 20, bottom: 60, left: 0 };

// Helper: convert 0–1 decimal to percentage for display
const pct = (v) => v != null ? (v * 100).toFixed(1) : '0.0';
const pctInt = (v) => v != null ? Math.round(v * 100) : 0;

// PE color coding based on score (0–1.0 scale)
const getPEColor = (score) => {
  if (score >= 0.80) return { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', fill: '#10b981', darkFill: '#047857' };
  if (score >= 0.60) return { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', fill: '#f59e0b', darkFill: '#b45309' };
  if (score >= 0.40) return { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', fill: '#f97316', darkFill: '#c2410c' };
  return { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200', fill: '#ef4444', darkFill: '#b91c1c' };
};

const componentColors = {
  capability: '#6366f1',
  motivation: '#f59e0b',
  retention: '#10b981',
  lei: '#8b5cf6',
  pe: '#0ea5e9',
};

const componentInfo = {
  capability: "Capability (C) measures the alignment of workforce skills at assessed proficiency levels versus role requirements. It's the 'engine size' of your People Engine. What to do: a low C means you have a *skills gap* — investment in learning content, certification programmes, or targeted hiring will move this number.",
  motivation: "Motivation (M) captures employee willingness to apply capabilities, shaped by intrinsic drivers (purpose, pride, growth) and extrinsic drivers (leadership, recognition, compensation). What to do: a low M means people CAN do the work but won't — fix the manager-coaching layer and recognition system before adding more training.",
  retention: "Retention (R) measures the stability of critical talent in role — whether employees stay long enough to reach and sustain full productivity. Inverse of organizational churn. What to do: a low R erodes everything else — even high C × M won't compound if people leave before they hit full productivity. Address career-path clarity and compensation benchmarks first.",
  lei: "Learning Effectiveness Index (LEI) represents the additive performance boost from learning programs, measured across 5 Pillars of effectiveness. What to do: a low LEI means your *learning ecosystem itself* is leaking — see Maturity Health to identify which of the 5 pillars (access / outcomes / capability / operations / business impact) is the bottleneck.",
  pe: "People Engine Capacity: PE = (((C × M) × R) + LE) / 2. Quantifies your workforce capacity to deliver business performance. What to do: read this as the single number to share with finance — it predicts roughly what % of theoretical business goals your workforce can actually deliver this period.",
  ppe: "People Performance Effectiveness: PPE = C × M. Combined measure of capability and motivation before retention and learning factors. What to do: PPE isolates the question 'do my people both *can* and *want to* do the work' — the gap between PPE and PE shows how much retention churn and learning quality drag the headline number.",
};

const DeltaIndicator = ({ value }) => {
  if (!value && value !== 0) return null;
  const isPositive = value > 0;
  const isZero = value === 0;
  const displayVal = (value * 100).toFixed(1);
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${isPositive ? 'text-emerald-600' : isZero ? 'text-slate-400' : 'text-rose-500'}`}>
      {isPositive ? <TrendingUp className="h-3 w-3" /> : isZero ? <Minus className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {isPositive ? '+' : ''}{displayVal}%
    </span>
  );
};

const InfoTooltip = ({ text }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <Info className="h-3.5 w-3.5 text-slate-400 hover:text-slate-600 cursor-help inline ml-1" />
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-xs">
        <p>{text}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

// Gauge component for PE score
const PEGauge = ({ score, label, size = 'lg' }) => {
  const color = getPEColor(score);
  const radius = size === 'lg' ? 80 : 40;
  const stroke = size === 'lg' ? 12 : 8;
  const circumference = Math.PI * radius;
  const progress = score * circumference;
  const svgW = radius * 2 + stroke * 2;
  const svgH = radius + stroke * 2 + 10;

  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: svgW, height: svgH }}>
        <svg width={svgW} height={svgH} viewBox={`0 0 ${svgW} ${svgH}`}>
          <path
            d={`M ${stroke} ${radius + stroke} A ${radius} ${radius} 0 0 1 ${radius * 2 + stroke} ${radius + stroke}`}
            fill="none" stroke="#e2e8f0" strokeWidth={stroke} strokeLinecap="round"
          />
          <path
            d={`M ${stroke} ${radius + stroke} A ${radius} ${radius} 0 0 1 ${radius * 2 + stroke} ${radius + stroke}`}
            fill="none" stroke={color.fill} strokeWidth={stroke} strokeLinecap="round"
            strokeDasharray={`${progress} ${circumference}`} className="transition-all duration-1000"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-end" style={{ paddingBottom: size === 'lg' ? 14 : 8 }}>
          <span className={`${size === 'lg' ? 'text-3xl' : 'text-lg'} font-bold text-slate-800`} data-testid="pe-gauge-score">
            {pct(score)}%
          </span>
          {size === 'lg' && <span className="text-xs text-slate-400">PE Score</span>}
        </div>
      </div>
      {label && <span className="text-xs text-slate-500 mt-1">{label}</span>}
    </div>
  );
};

// Sparkline for alert trend context
const AlertSparkline = ({ data, component }) => {
  if (!data || data.length < 2) return null;
  const values = data.map(d => d[component] || d.pe || 0);
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;
  const w = 80, h = 24;
  const points = values.map((v, i) => `${(i / (values.length - 1)) * w},${h - ((v - min) / range) * h}`).join(' ');
  return (
    <svg width={w} height={h} className="inline-block ml-2">
      <polyline points={points} fill="none" stroke="#94a3b8" strokeWidth="1.5" />
      <circle cx={(values.length - 1) / (values.length - 1) * w} cy={h - ((values[values.length - 1] - min) / range) * h} r="2" fill="#ef4444" />
    </svg>
  );
};

// CSV Import Panel
const CSVImportPanel = ({ authHeaders }) => {
  const [showImport, setShowImport] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState([]);
  const fileRef = React.useRef(null);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const newResults = [];

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await authedFetch(`${API_URL}/api/people-engine/import/csv`, {
          method: 'POST',
          headers: { 'Authorization': authHeaders['Authorization'] },
          body: formData,
        });
        const data = await res.json();
        if (res.ok) {
          newResults.push({ file: file.name, status: 'success', imported: data.imported, type: data.type, errors: data.errors, createdCohorts: data.created_cohorts });
          const extra = data.created_cohorts ? ` (${data.created_cohorts} new cohort${data.created_cohorts > 1 ? 's' : ''} created)` : '';
          toast.success(`Imported ${data.imported} ${data.type} records from ${file.name}${extra}`);
        } else {
          newResults.push({ file: file.name, status: 'error', message: data.detail || 'Upload failed' });
          toast.error(`${file.name}: ${data.detail || 'Upload failed'}`);
        }
      } catch (err) {
        logger.error('CSV upload failed:', err);
        newResults.push({ file: file.name, status: 'error', message: 'Network error' });
        toast.error(`${file.name}: Upload failed`);
      }
    }
    setResults(newResults);
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div className="flex items-center gap-2">
      <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5 border-slate-300 text-slate-700 dark:border-slate-500 dark:text-slate-200 dark:hover:bg-slate-700" onClick={() => setShowImport(!showImport)} data-testid="pe-import-toggle">
        <Upload className="h-3.5 w-3.5" /> Import CSV
      </Button>
      <a href="/downloads/pe_capability_data.csv" download className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors">
        <Download className="h-3 w-3" /> Templates
      </a>

      {showImport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setShowImport(false)}>
          <Card className="w-full max-w-lg mx-4" onClick={e => e.stopPropagation()} data-testid="pe-import-dialog">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><FileUp className="h-4 w-4" /> Import PE Data (CSV)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-sm text-slate-500 space-y-1">
                <p>Upload CSV files for <strong>Capability</strong>, <strong>Motivation</strong>, <strong>Retention</strong>, or <strong>LEI</strong> data. The file type is auto-detected from column headers.</p>
                <p className="text-xs">Required columns: <code>organization_name, cohort_name, period</code> + score columns. All scores on 0–1 scale. IDs also accepted.</p>
              </div>

              <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
                <input ref={fileRef} type="file" accept=".csv" multiple onChange={handleUpload} className="hidden" id="csv-upload" data-testid="csv-file-input" />
                <label htmlFor="csv-upload" className="cursor-pointer">
                  <Upload className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                  <p className="text-sm text-slate-500">{uploading ? 'Uploading...' : 'Click to select CSV files'}</p>
                  <p className="text-xs text-slate-400 mt-1">Supports multiple files at once</p>
                </label>
              </div>

              {results.length > 0 && (
                <div className="space-y-2" data-testid="import-results">
                  {results.map((r, idx) => (
                    <div key={r.file + idx} className={`p-2 rounded text-xs ${r.status === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                      <span className="font-medium">{r.file}</span>: {r.status === 'success' ? `${r.imported} ${r.type} records imported` : r.message}
                      {r.errors && r.errors.length > 0 && (
                        <ul className="mt-1 ml-4 list-disc text-amber-600">
                          {r.errors.map((e, i) => <li key={`err-${i}`}>{e}</li>)}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-2 text-xs text-slate-400">
                <span>Download templates:</span>
                <a href="/downloads/pe_capability_data.csv" download className="underline hover:text-slate-600">Capability</a>
                <a href="/downloads/pe_motivation_data.csv" download className="underline hover:text-slate-600">Motivation</a>
                <a href="/downloads/pe_retention_data.csv" download className="underline hover:text-slate-600">Retention</a>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// PE Explanation Panel (collapsible)
const PEExplainer = () => {
  const [expanded, setExpanded] = useState(false);
  return (
    <Card className="border-slate-200 bg-gradient-to-b from-slate-50 to-white" data-testid="pe-explainer">
      <CardContent className="pt-4 pb-3">
        <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between group" data-testid="pe-explainer-toggle">
          <div className="flex items-center gap-2">
            <Info className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-semibold text-slate-800">What is the People Engine and Why Does It Matter?</span>
          </div>
          <ChevronRight className={`h-4 w-4 text-slate-400 group-hover:text-slate-600 transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </button>
        {expanded && (
          <div className="mt-4 space-y-4" data-testid="pe-explainer-content">
            <p className="text-sm text-slate-600 leading-relaxed">
              Most organizations measure learning by completions and satisfaction. The People Engine measures what actually matters:
              <strong> can your workforce deliver the business outcomes you need?</strong> It connects learning metrics directly
              to business performance through a quantified value chain.
            </p>

            {/* Component breakdown */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-3 bg-indigo-50 border border-indigo-100 rounded-lg">
                <p className="text-xs font-bold text-indigo-800">Capability (C)</p>
                <p className="text-[10px] text-indigo-600 mt-0.5">Skills alignment vs role requirements. Are people skilled enough to perform?</p>
              </div>
              <div className="p-3 bg-amber-50 border border-amber-100 rounded-lg">
                <p className="text-xs font-bold text-amber-800">Motivation (M)</p>
                <p className="text-[10px] text-amber-600 mt-0.5">Willingness to apply skills. Driven by purpose, recognition, confidence, and manager support.</p>
              </div>
              <div className="p-3 bg-emerald-50 border border-emerald-100 rounded-lg">
                <p className="text-xs font-bold text-emerald-800">Retention (R)</p>
                <p className="text-[10px] text-emerald-600 mt-0.5">Talent stability. If skilled, motivated people leave, your PE capacity walks out the door.</p>
              </div>
              <div className="p-3 bg-purple-50 border border-purple-100 rounded-lg">
                <p className="text-xs font-bold text-purple-800">LEI (LE)</p>
                <p className="text-[10px] text-purple-600 mt-0.5">Learning system effectiveness. The additive boost from how well your L&D programs actually work.</p>
              </div>
            </div>

            {/* How it connects */}
            <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg">
              <p className="text-xs font-bold text-blue-800">How PE Connects to Business Outcomes</p>
              <p className="text-[10px] text-blue-600 mt-1 leading-relaxed">
                <strong>PE Ideal</strong> (C × M) predicts theoretical workforce capacity.
                <strong> ROE</strong> validates what percentage actually translates to behavior change.
                <strong> PE Validated</strong> = PE Ideal × ROE — this is your proven capacity, which drives
                <strong> KPI impact</strong> and ultimately <strong>financial ROI</strong>.
                A PE Score of 50% means half your workforce's potential is being realized. The gap is your improvement opportunity.
              </p>
            </div>

            <p className="text-xs text-slate-500 border-t border-slate-100 pt-3">
              <strong>Reading this dashboard:</strong> Each component card shows the current score and period-over-period change (delta).
              Click any component to see a detailed breakdown and AI-powered diagnostic. Below, the PPE row shows C × M before retention/LEI adjustments,
              and the ROE Impact section shows how actual behavior data validates your PE score.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// PE Alerts Banner
const PEAlertsBanner = ({ orgId, authHeaders }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  const fetchAlerts = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const res = await authedFetch(`${API_URL}/api/people-engine/alerts?organization_id=${orgId}&status=active`, { headers: authHeaders });
      if (res.ok) {
        const data = await res.json();
        setAlerts(data.alerts || []);
      }
    } catch (err) { logger.error('Failed to fetch alerts:', err); }
    setLoading(false);
  }, [orgId, authHeaders]);

  const fetchSettings = useCallback(async () => {
    if (!orgId) return;
    try {
      const res = await authedFetch(`${API_URL}/api/people-engine/alerts/settings?organization_id=${orgId}`, { headers: authHeaders });
      if (res.ok) setSettings(await res.json());
    } catch (err) { logger.error('Failed to fetch alert settings:', err); }
  }, [orgId, authHeaders]);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const dismissAlert = async (alertId) => {
    try {
      const res = await authedFetch(`${API_URL}/api/people-engine/alerts/${alertId}/dismiss`, {
        method: 'PATCH', headers: authHeaders,
      });
      if (res.ok) {
        setAlerts(prev => prev.filter(a => a.id !== alertId));
        toast.success('Alert dismissed');
      }
    } catch { toast.error('Failed to dismiss alert'); }
  };

  const runCheck = async () => {
    setLoading(true);
    try {
      await authedFetch(`${API_URL}/api/people-engine/alerts/check-now?organization_id=${orgId}`, {
        method: 'POST', headers: authHeaders,
      });
      await fetchAlerts();
      toast.success('Alert check completed');
    } catch { toast.error('Alert check failed'); }
    setLoading(false);
  };

  const saveSettings = async () => {
    try {
      const res = await authedFetch(`${API_URL}/api/people-engine/alerts/settings`, {
        method: 'PUT',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...settings, organization_id: orgId }),
      });
      if (res.ok) {
        toast.success('Alert settings saved');
        setShowSettings(false);
      }
    } catch { toast.error('Failed to save settings'); }
  };

  if (alerts.length === 0 && !showSettings) return null;

  const criticalCount = alerts.filter(a => a.severity === 'critical').length;
  const warningCount = alerts.filter(a => a.severity === 'warning').length;

  return (
    <Card className="border-l-4 border-l-amber-400 bg-amber-50/40" data-testid="pe-alerts-banner">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => setCollapsed(!collapsed)} data-testid="pe-alerts-toggle">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <span className="text-sm font-semibold text-amber-800">
              PE Alerts
              {alerts.length > 0 && (
                <span className="ml-2 text-xs font-normal">
                  {criticalCount > 0 && <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-rose-100 text-rose-700 mr-1">{criticalCount} critical</span>}
                  {warningCount > 0 && <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-700">{warningCount} warning</span>}
                </span>
              )}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-slate-500" onClick={runCheck} disabled={loading} data-testid="pe-alerts-refresh">
              <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Check Now
            </Button>
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-slate-500" onClick={() => { setShowSettings(!showSettings); if (!settings) fetchSettings(); }} data-testid="pe-alerts-settings-btn">
              <Settings2 className="h-3 w-3" />
            </Button>
          </div>
        </div>

        {/* Settings panel */}
        {showSettings && settings && (
          <div className="mb-3 p-3 bg-white rounded-lg border border-slate-200" data-testid="pe-alerts-settings">
            <p className="text-xs font-medium text-slate-700 mb-2">Alert Thresholds</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <label className="text-xs text-slate-500">
                PE Threshold
                <input type="number" className="mt-0.5 block w-full px-2 py-1 text-sm border border-slate-200 rounded" value={settings.pe_threshold}
                  onChange={e => setSettings({ ...settings, pe_threshold: +e.target.value })} data-testid="pe-threshold-input" />
              </label>
              <label className="text-xs text-slate-500">
                Component Threshold
                <input type="number" className="mt-0.5 block w-full px-2 py-1 text-sm border border-slate-200 rounded" value={settings.component_threshold}
                  onChange={e => setSettings({ ...settings, component_threshold: +e.target.value })} data-testid="component-threshold-input" />
              </label>
              <label className="text-xs text-slate-500">
                Decline Months
                <input type="number" min="2" max="12" className="mt-0.5 block w-full px-2 py-1 text-sm border border-slate-200 rounded" value={settings.decline_months}
                  onChange={e => setSettings({ ...settings, decline_months: +e.target.value })} data-testid="decline-months-input" />
              </label>
              <div className="flex items-end">
                <Button size="sm" className="h-7 text-xs" onClick={saveSettings} data-testid="save-alert-settings-btn">Save</Button>
              </div>
            </div>
          </div>
        )}

        {/* Alert items */}
        {!collapsed && alerts.length > 0 && (
          <div className="space-y-2" data-testid="pe-alerts-list">
            {alerts.map(alert => (
              <div key={alert.id} className={`flex items-center justify-between p-2.5 rounded-lg border ${
                alert.severity === 'critical' ? 'bg-rose-50 border-rose-200' : 'bg-amber-50 border-amber-200'
              }`} data-testid={`pe-alert-item-${alert.id}`}>
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className={`shrink-0 w-1.5 h-8 rounded-full ${alert.severity === 'critical' ? 'bg-rose-500' : 'bg-amber-400'}`} />
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className={`text-[10px] font-semibold uppercase tracking-wider ${alert.severity === 'critical' ? 'text-rose-600' : 'text-amber-600'}`}>
                        {alert.severity}
                      </span>
                      <span className="text-[10px] text-slate-400 capitalize">{alert.alert_type?.replace('_', ' ')}</span>
                    </div>
                    <p className="text-sm text-slate-700 truncate">{alert.message}</p>
                    {alert.organization_name && <p className="text-[10px] text-slate-400">{alert.organization_name}</p>}
                  </div>
                  <AlertSparkline data={alert.trend_snapshot} component={alert.component} />
                </div>
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0 shrink-0 ml-2 text-slate-400 hover:text-slate-600" onClick={() => dismissAlert(alert.id)} data-testid={`dismiss-alert-${alert.id}`}>
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function PEHealthPage() {
  const { isSuperAdmin, viewAsOrg } = useAuth();
  const location = useLocation();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme !== 'light';
  const segs = location.pathname.split('/').filter(Boolean);
  const subView = segs[1] === 'inventory' ? 'inventory'
    : segs[1] === 'coverage' ? 'coverage'
    : segs[1] === 'trends' ? 'trends'
    : (segs[1] === 'deep-dives' || segs[1] === 'health') ? 'deep-dives' : 'overview';
  // Deep-link from Overview Alerts & Risks → scroll to the action plan on Priorities.
  useEffect(() => {
    if (subView === 'deep-dives' && location.state?.focusComponent) {
      const t = setTimeout(() => {
        const el = document.querySelector('[data-testid="pe-priority-focus-section"]');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 500);
      return () => clearTimeout(t);
    }
  }, [subView, location.state]);
  // Deep-link from a Cohort PE Comparison bar → select that cohort + scroll to the component breakdown.
  useEffect(() => {
    if (subView === 'deep-dives' && location.state?.focusCohort) {
      setSelectedCohort(location.state.focusCohort);
      const t = setTimeout(() => {
        const el = document.querySelector('[data-testid="pe-deep-dives-section"]');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 500);
      return () => clearTimeout(t);
    }
  }, [subView, location.state]);

  // Deep-link: ?organization_id= scopes the page and (for super admins)
  // aligns the global "Previewing as" banner. Backend guards tenant access.
  const urlOrgId = useDeepLinkOrg();
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(urlOrgId || viewAsOrg?.id || '');
  const [selectedCohort, setSelectedCohort] = useState('all');
  const [healthData, setHealthData] = useState(null);
  const [roeHealth, setRoeHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  // Org-wide annual L&D-attributable value at stake — used to
  // convert the weakest PE component's score into a concrete
  // $/year leaking + $/year unlocked pair under the weak-link
  // callout. Same endpoint LEI/Maturity Health already use, so
  // the dollar figure reconciles across engines.
  const [valueAtStake, setValueAtStake] = useState(null);
  const peQuarterDelta = useQuarterDelta('pe');

  const headers = useCallback(() => {
    const h = { 'Authorization': `Bearer ${secureStorage.getItem('session_token')}` };
    if (viewAsOrg?.id) h['X-View-As-Org'] = viewAsOrg.id;
    return h;
  }, [viewAsOrg]);

  // Fetch organizations
  useEffect(() => {
    const fetchOrgs = async () => {
      try {
        const res = await api.get('/people-engine/organizations');
        const data = res.data;
        setOrganizations(data.organizations || []);
        setSelectedOrg(prev => prev || (data.organizations?.length > 0 ? data.organizations[0].id : ''));
      } catch (err) { logger.error('Failed to fetch organizations:', err); }
    };
    fetchOrgs();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch cohorts when org changes
  useEffect(() => {
    if (!selectedOrg) return;
    const fetchCohorts = async () => {
      try {
        const res = await api.get(`/people-engine/cohorts?organization_id=${selectedOrg}`);
        setCohorts(res.data?.cohorts || []);
      } catch (err) { logger.error('Failed to fetch cohorts:', err); }
    };
    fetchCohorts();
  }, [selectedOrg]);

  // Fetch the org's annual value at stake when the selected org
  // changes. We reuse `/lei-health/value-at-stake` because the
  // computation is engine-agnostic (revenue × margin × 5%) —
  // same number drives LEI, Maturity, and now PE dollar framing.
  useEffect(() => {
    if (!selectedOrg) { setValueAtStake(null); return; }
    let cancelled = false;
    api.get(`/lei-health/value-at-stake?organization_id=${encodeURIComponent(selectedOrg)}`)
      .then((res) => {
        if (!cancelled && res?.data?.annual_value_at_stake_usd != null) {
          setValueAtStake(res.data);
        }
      })
      .catch(() => { /* silent — page still works without dollars */ });
    return () => { cancelled = true; };
  }, [selectedOrg]);

  // Fetch health data + ROE health
  useEffect(() => {
    if (!selectedOrg) return;
    const fetchHealth = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ organization_id: selectedOrg });
        if (selectedCohort !== 'all') params.append('cohort_id', selectedCohort);
        const res = await api.get(`/people-engine/health?${params}`);
        setHealthData(res.data);
        // Also fetch ROE health for this org
        try {
          const roeRes = await api.get(`/roe/actuals/health?organization_id=${selectedOrg}`);
          setRoeHealth(roeRes.data);
        } catch (roeErr) { logger.error('ROE health fetch failed:', roeErr); setRoeHealth(null); }
      } catch (e) {
        logger.error('Failed to load PE data:', e);
        toast.error('Failed to load PE data');
      }
      setLoading(false);
    };
    fetchHealth();
  }, [selectedOrg, selectedCohort]);

  const current = healthData?.current;
  const delta = healthData?.delta;
  const trends = healthData?.trends || [];
  const cohortScores = healthData?.cohort_scores || [];
  const theme = isDark
    ? { ...getSectionTheme('PE'), heroGradient: 'bg-[#0a0f1c]', bodyTint: 'bg-[#0a0f1c]', textBase: 'text-white', textLighter: 'text-slate-400', titleClass: 'text-slate-50 font-extrabold' }
    : getLightSectionTheme('PE');

  const heroActions = (
    <>
      <PageToolbar theme={theme} collapsePrefix="pe" organizationId={selectedOrg} testId="pe-toolbar" />
      <Button variant="outline" size="sm" onClick={() => navigate('/people-engine/reference')} className="gap-1.5 bg-white border-slate-200 text-slate-700 hover:bg-slate-50">
        <BookOpen className="h-4 w-4" />
        PE Reference
      </Button>
    </>
  );

  // Per-component sparklines reused from the trend history when
  // 2+ snapshots exist. Each stat also ships a whyPanel so a
  // single click opens the breakdown side-panel.
  const peSparkline = (key) => Array.isArray(history) && history.length > 1
    ? [...history].slice(0, 6).reverse().map((h) => ({
        label: (h.assessed_at || h.created_at || '').slice(0, 10),
        value: pct(h[key] || 0),
      }))
    : null;
  const heroStats = current ? [
    {
      label: 'Capability',
      value: `${pct(current.capability || 0)}%`,
      testId: 'pe-hero-cap',
      sparkline: peSparkline('capability'),
      sparklineFormatter: (v) => `${v}%`,
      ...(typeof delta?.capability === 'number' && delta.capability !== 0 ? {
        delta: Number(delta.capability.toFixed(1)),
        deltaText: `${delta.capability > 0 ? '+' : ''}${delta.capability.toFixed(1)} pts`,
        deltaLabel: 'vs previous',
      } : {}),
      whyPanel: {
        label: 'Capability',
        value: `${pct(current.capability || 0)}%`,
        description: 'Workforce mastery of the skills the business needs — composite of formal assessment scores, certifications, and demonstrated proficiency.',
        formula: 'mean(assessment_score) weighted by skill_priority',
        links: [
          { label: 'Set Capability targets', path: '/people-engine/goals' },
          { label: 'Open PE Diagnostic', path: '/people-engine/diagnostic' },
        ],
      },
    },
    {
      label: 'Motivation',
      value: `${pct(current.motivation || 0)}%`,
      testId: 'pe-hero-mot',
      sparkline: peSparkline('motivation'),
      sparklineFormatter: (v) => `${v}%`,
      ...(typeof delta?.motivation === 'number' && delta.motivation !== 0 ? {
        delta: Number(delta.motivation.toFixed(1)),
        deltaText: `${delta.motivation > 0 ? '+' : ''}${delta.motivation.toFixed(1)} pts`,
        deltaLabel: 'vs previous',
      } : {}),
      whyPanel: {
        label: 'Motivation',
        value: `${pct(current.motivation || 0)}%`,
        description: 'Engagement and intent to apply skills on the job — measured via pulse surveys, training feedback, and manager-observed effort.',
        formula: 'engagement_index × intent_to_apply_score',
        links: [
          { label: 'Open PE Diagnostic', path: '/people-engine/diagnostic' },
        ],
      },
    },
    {
      label: 'Retention',
      value: `${pct(current.retention || 0)}%`,
      testId: 'pe-hero-ret',
      sparkline: peSparkline('retention'),
      sparklineFormatter: (v) => `${v}%`,
      ...(typeof delta?.retention === 'number' && delta.retention !== 0 ? {
        delta: Number(delta.retention.toFixed(1)),
        deltaText: `${delta.retention > 0 ? '+' : ''}${delta.retention.toFixed(1)} pts`,
        deltaLabel: 'vs previous',
      } : {}),
      whyPanel: {
        label: 'Retention',
        value: `${pct(current.retention || 0)}%`,
        description: 'Workforce stability over the period — high retention compounds the learning investment because the same people stay long enough to apply what they learned.',
        formula: '1 - (regretted_attrition_rate)',
        links: [
          { label: 'Open PE Diagnostic', path: '/people-engine/diagnostic' },
        ],
      },
    },
  ] : null;

  // North-star — PE Score promoted from a small stat into the
  // CFO-grade centerpiece. Sparkline reuses the trend history
  // when 2+ snapshots exist.
  const heroNorthStar = current ? {
    label: 'PE Score · workforce delivery capacity',
    value: `${pct(current.pe || 0)}`,
    valueSuffix: '%',
    description: (current.band
      ? `${current.band} band · composite of capability, motivation, retention`
      : 'Composite of capability, motivation, retention'),
    percentile: typeof current.percentile === 'number' ? current.percentile : undefined,
    percentileLabel: current.peer_benchmark?.industry_name
      ? `${current.peer_benchmark.industry_name} percentile`
      : 'Industry percentile',
    peerLabel: 'Peer median',
    peerValue: current.peer_median ? `${pct(current.peer_median)}%` : undefined,
    sparklineData: Array.isArray(history) && history.length > 1
      ? [...history].slice(0, 6).reverse().map((h) => ({
          label: (h.assessed_at || h.created_at || '').slice(0, 10),
          value: pct(h.pe || h.score || 0),
        }))
      : null,
    sparklineFormatter: (v) => `${v}%`,
    testId: 'pe-hero-northstar',
    ...(peQuarterDelta ? {
      delta: peQuarterDelta.delta,
      deltaText: peQuarterDelta.deltaText,
      deltaLabel: peQuarterDelta.deltaLabel,
    } : (delta?.pe ? {
      delta: Number(delta.pe.toFixed(1)),
      deltaText: `${delta.pe > 0 ? '+' : ''}${delta.pe.toFixed(1)} pts`,
      deltaLabel: 'vs previous',
    } : {})),
    whyPanel: {
      label: 'PE Score',
      value: `${pct(current.pe || 0)}`,
      valueSuffix: '%',
      description: (
        'The People Engine score is a composite that quantifies your '
        + 'workforce capacity to deliver business outcomes. It '
        + 'multiplies Capability × Motivation × Retention, then '
        + 'weights the result by Learning Effectiveness so a team '
        + 'that learns well keeps compounding while a team that '
        + 'doesn\'t plateaus.'
      ),
      formula: 'Capability × Motivation × Retention × LearningEffectiveness',
      breakdown: [
        { label: 'Capability', value: `${pct(current.capability || 0)}%`, hint: 'skill mastery + assessment scores' },
        { label: 'Motivation', value: `${pct(current.motivation || 0)}%`, hint: 'engagement surveys + intent signals' },
        { label: 'Retention', value: `${pct(current.retention || 0)}%`, hint: 'workforce stability over the period' },
        ...(typeof current.learning_effectiveness === 'number'
          ? [{ label: 'Learning Effectiveness', value: `${pct(current.learning_effectiveness)}%`, hint: 'transfer × applied score' }]
          : []),
      ],
      history: Array.isArray(history) && history.length
        ? [...history].slice(0, 4).reverse().map((h) => ({
            label: (h.assessed_at || h.created_at || '').slice(0, 10),
            value: `${pct(h.pe || h.score || 0)}%`,
          }))
        : null,
      links: [
        { label: 'Open PE Diagnostic', path: '/people-engine/diagnostic' },
        { label: 'Browse PE Reference', path: '/people-engine/reference' },
        { label: 'Set PE Goals', path: '/people-engine/goals' },
      ],
    },
  } : null;

  // Page-scoped narrative — biggest gainer vs biggest decliner
  // across capability/motivation/retention components.
  const heroNarrative = (current && delta) ? (() => {
    const components = [
      typeof delta.capability === 'number' && delta.capability !== 0
        ? { label: 'Capability', delta: delta.capability, deltaText: `${delta.capability > 0 ? '+' : ''}${delta.capability.toFixed(1)} pts` }
        : null,
      typeof delta.motivation === 'number' && delta.motivation !== 0
        ? { label: 'Motivation', delta: delta.motivation, deltaText: `${delta.motivation > 0 ? '+' : ''}${delta.motivation.toFixed(1)} pts` }
        : null,
      typeof delta.retention === 'number' && delta.retention !== 0
        ? { label: 'Retention', delta: delta.retention, deltaText: `${delta.retention > 0 ? '+' : ''}${delta.retention.toFixed(1)} pts` }
        : null,
    ].filter(Boolean);
    if (!components.length) return null;
    const sortedDesc = [...components].sort((a, b) => b.delta - a.delta);
    const gainer = sortedDesc[0]?.delta > 0 ? sortedDesc[0] : null;
    const decliner = sortedDesc[sortedDesc.length - 1]?.delta < 0 ? sortedDesc[sortedDesc.length - 1] : null;
    if (!gainer && !decliner) return null;
    return { gainer, decliner };
  })() : null;
  return (
    <PageShell
      theme={theme}
      stickyTitle="Capability Health"
      hero={(
        <PageHero
          title="Capability Health"
          titleHint="PE"
          description="The People Engine (PE) quantifies your workforce capacity to deliver business performance — the CAPABILITY stage of the Impact Chain — combining Capability, Motivation, Retention, and Learning Effectiveness into a single score."
          theme={theme}
          icon={Users}
          actions={heroActions}
          northStar={undefined}
          narrative={undefined}
          stats={undefined}
          testId="pe-hero"
        >
          {/* Light hero accents — thin teal brand rule + faint
              indigo/teal radial glow. Matches LIE home / LEI Health. */}
          <div
            className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-teal-400 via-teal-500 to-teal-400 pointer-events-none"
            aria-hidden="true"
          />
          <div
            className="absolute inset-0 opacity-[0.08] pointer-events-none"
            style={{
              backgroundImage:
                'radial-gradient(circle at 20% 50%, #6366f1 0%, transparent 50%), ' +
                'radial-gradient(circle at 80% 50%, #14b8a6 0%, transparent 50%)',
            }}
            aria-hidden="true"
          />
        </PageHero>
      )}
    >
      <div className={cn('space-y-6', isDark ? 'home-dark-scope' : 'mc-light')} data-testid="pe-health-page">
      <PageSubNav items={PE_SUBNAV} rightSlot={(subView === 'inventory' || subView === 'coverage') ? <CapabilityDataButton organizationId={selectedOrg} /> : null} />

      {/* Inventory / Coverage — the Capability Inventory experience embedded as sub-nav tabs.
          key={subView} forces a remount so the view switches when the tab changes. */}
      {(subView === 'inventory' || subView === 'coverage') && (
        <div>
          {subView === 'coverage' && (
            <div className="mb-4" data-testid="coverage-header">
              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-teal-600">Coverage</p>
              <h2 className="text-lg font-bold text-slate-800 mt-0.5">Role Coverage &amp; Bench Readiness</h2>
              <p className="text-sm text-slate-500 mt-1 max-w-3xl">How well your workforce covers the roles the business depends on — which roles have a ready holder, where the bench is thin, and the succession risk if a key person leaves. (The Inventory tab lists <em>who</em> has <em>which</em> skills; this tab measures whether those skills add up to covered roles.)</p>
            </div>
          )}
          <CapabilityInventoryPage key={subView} embedded coverageMode={subView === 'coverage'} initialView={subView === 'coverage' ? 'analytics' : 'inventory'} />
        </div>
      )}

      {/* Overview — Lean dark KPI overview + component breakdown + action plan */}
      {subView === 'overview' && !loading && current && (
        <>
          <PeLeanOverview
            current={current}
            delta={delta}
            valueAtStake={valueAtStake}
            selectedOrg={selectedOrg}
            selectedCohort={selectedCohort}
            navigate={navigate}
          />
          <PeCohortComparison
            cohortScores={cohortScores}
            cohortScoresPrev={healthData?.cohort_scores_prev || []}
            prevPeriod={healthData?.prev_period}
            isDark={isDark}
            onCohortClick={(cid) => { setSelectedCohort(cid); navigate('/people-engine/deep-dives', { state: { focusCohort: cid } }); }}
            onCohortTrends={(cid) => { setSelectedCohort(cid); navigate('/people-engine/trends', { state: { focusCohort: cid } }); }}
            testId="pe-overview-cohort-comparison"
          />
        </>
      )}

      {/* Trends — full PE Score Trends board (composite + component + compare + AI narrator) */}
      {subView === 'trends' && !loading && (
        <section data-testid="pe-trends-section">
          <div className="mb-4">
            <h2 className={`text-lg font-bold leading-tight ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>Explore PE Trends</h2>
            <p className={`text-xs mt-1 max-w-3xl ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Composite People Engine capacity with Capability, Motivation &amp; Retention lines — compare cohorts, programs or periods, with an AI read of what's driving the movement</p>
          </div>
          <PeTrendsFull organizationId={selectedOrg} cohortId={selectedCohort} navigate={navigate} />
        </section>
      )}

      {subView === 'deep-dives' && (
      <>
      <CollapsibleSection
        storageKey="pe.deepDives"
        title={`Explore the ${PE_SUBNAV.find((i) => i.id === subView)?.label || 'Details'}`}
        accent="text-indigo-600"
        defaultExpanded
        testId="pe-deep-dives-section"
        meta={(
          <FilterSummaryChip
            testId="pe-deepdive-filter-chip"
            parts={[
              selectedOrg ? organizations.find((o) => o.id === selectedOrg)?.name : null,
              selectedCohort !== 'all' ? cohorts.find((c) => c.id === selectedCohort)?.name : null,
            ]}
          />
        )}
      >
          <div className="space-y-4">
          {/* Filters + CSV import — contained within Deep Dives */}
          <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="pe-deepdive-controls">
            <div className="flex items-center gap-3" data-testid="pe-filters">
              {isSuperAdmin && organizations.length > 0 && (
                <Select value={selectedOrg} onValueChange={(v) => { setSelectedOrg(v); setSelectedCohort('all'); }}>
                  <SelectTrigger className="w-64" data-testid="pe-org-filter">
                    <SelectValue placeholder="Select organization..." />
                  </SelectTrigger>
                  <SelectContent>
                    {organizations.map(org => (
                      <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Select value={selectedCohort} onValueChange={setSelectedCohort}>
                <SelectTrigger className="w-56" data-testid="pe-cohort-filter">
                  <SelectValue placeholder="All Cohorts" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Cohorts</SelectItem>
                  {cohorts.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <CSVImportPanel authHeaders={headers()} />
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin h-8 w-8 border-2 border-indigo-500 border-t-transparent rounded-full" />
            </div>
          ) : current ? (
            <>
          {/* PE Score + Component Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            {/* Main PE Gauge */}
            <Card className={`lg:col-span-1 ${getPEColor(current.pe).bg} ${getPEColor(current.pe).border} border`} data-testid="pe-main-gauge">
              <CardContent className="p-6 flex flex-col items-center">
                <PEGauge score={current.pe} size="lg" />
                <h3 className="text-lg font-bold text-slate-900 mt-2 flex items-center gap-1">
                  PE Score
                  <TileInfoButton
                    testid="pe-score-info"
                    title="People Engine Score"
                    body={componentInfo.pe}
                  />
                </h3>
                {delta && <DeltaIndicator value={delta.pe} />}
                <p className="text-xs text-slate-500 mt-1 text-center">
                  ~{pctInt(current.pe)}% of business goals achievable
                </p>
              </CardContent>
            </Card>

            {/* Component Cards */}
            {[
              { key: 'capability', icon: Brain, label: 'Capability (C)', score: current.capability, deltaVal: delta?.capability },
              { key: 'motivation', icon: Heart, label: 'Motivation (M)', score: current.motivation, deltaVal: delta?.motivation },
              { key: 'retention', icon: Shield, label: 'Retention (R)', score: current.retention, deltaVal: delta?.retention },
              { key: 'lei', icon: Activity, label: 'LEI (LE)', score: current.lei, deltaVal: delta?.lei },
            ].map(comp => (
              <Card
                key={comp.key}
                className="cursor-pointer hover:shadow-md transition-shadow border border-slate-200"
                onClick={() => navigate(`/people-engine/diagnostic?component=${comp.key}&org=${selectedOrg}`)}
                data-testid={`pe-card-${comp.key}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 rounded-lg" style={{ backgroundColor: componentColors[comp.key] + '15' }}>
                        <comp.icon className="h-4 w-4" style={{ color: componentColors[comp.key] }} />
                      </div>
                      <span className="text-sm font-medium text-slate-700 flex items-center gap-1">
                        {comp.label}
                        <TileInfoButton
                          testid={`pe-card-${comp.key}-info`}
                          title={comp.label}
                          body={componentInfo[comp.key]}
                        />
                      </span>
                    </div>
                    <ChevronRight className="h-4 w-4 text-slate-300" />
                  </div>
                  <div className="flex items-end justify-between">
                    <div>
                      <span className={`text-2xl font-bold ${getPEColor(comp.score).text}`}>
                        {pct(comp.score)}%
                      </span>
                    </div>
                    <DeltaIndicator value={comp.deltaVal} />
                  </div>
                  <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{ width: `${comp.score * 100}%`, backgroundColor: componentColors[comp.key] }}
                    />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* PPE Card */}
          <Card className="border border-slate-200">
            <CardContent className="p-4 flex items-center gap-6">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-600 flex items-center gap-1">
                  People Performance Effectiveness (PPE)
                  <TileInfoButton
                    testid="pe-ppe-info"
                    title="People Performance Effectiveness"
                    body={componentInfo.ppe}
                  />
                </span>
              </div>
              <span className={`text-xl font-bold ${getPEColor(current.ppe).text}`}>{pct(current.ppe)}%</span>
              <span className="text-xs text-slate-400">= C ({pct(current.capability)}%) x M ({pct(current.motivation)}%)</span>
            </CardContent>
          </Card>

          {/* ROE Impact on PE */}
          {roeHealth?.has_data ? (
            <Card className={isDark ? 'border border-purple-500/20' : 'border-2 border-purple-200'} data-testid="pe-roe-impact">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <ClipboardCheck className="h-4 w-4 text-purple-600" />
                  <span className="text-sm font-semibold text-slate-800">ROE Impact on PE</span>
                  <InfoTooltip text="ROE validates how much of your theoretical PE actually translates into workplace behavior. PE Ideal assumes 100% transfer. PE Validated = PE Ideal × ROE." />
                </div>
                <div className="grid grid-cols-4 gap-3">
                  <div className="text-center p-3 rounded-lg bg-slate-50 border relative">
                    <div className="absolute top-1 right-1">
                      <TileInfoButton
                        testid="pe-roe-impact-ideal-info"
                        title="PE Ideal"
                        body="The textbook People Engine score: assumes 100% of theoretical capability transfers to the workplace. This is the ceiling — what your workforce could deliver if every minute of training translated perfectly into on-the-job behavior. In practice, almost nobody hits 100% transfer; that's why we measure ROE separately."
                      />
                    </div>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">PE Ideal</p>
                    <p className={`text-xl font-bold ${getPEColor(current.pe).text}`}>{pct(current.pe)}%</p>
                    <p className="text-[10px] text-slate-400">assumes ROE = 100%</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-slate-50 border relative" style={{ borderColor: roeHealth.roe_actual.pct >= 55 ? '#14b8a6' : roeHealth.roe_actual.pct >= 30 ? '#f59e0b' : '#ef4444' }}>
                    <div className="absolute top-1 right-1">
                      <TileInfoButton
                        testid="pe-roe-impact-score-info"
                        title="ROE Score"
                        body="Return on Expectation — the measured percentage of training that actually shows up as changed on-the-job behavior. Computed from L2 (post-training assessment) and L3 (manager observations) signals. Below 30% is critical (workforce learned it but didn't do it differently); 55%+ is healthy."
                        linkLabel="Open ROE Health"
                        onLink={() => navigate('/roe-health')}
                      />
                    </div>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">ROE Score</p>
                    <p className="text-xl font-bold" style={{ color: roeHealth.roe_actual.pct >= 55 ? '#14b8a6' : roeHealth.roe_actual.pct >= 30 ? '#f59e0b' : '#ef4444' }}>{roeHealth.roe_actual.pct}%</p>
                    <p className="text-[10px] text-slate-400">from L2 + L3 actuals</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-purple-50 border-2 border-purple-300 relative">
                    <div className="absolute top-1 right-1">
                      <TileInfoButton
                        testid="pe-roe-impact-validated-info"
                        title="PE Validated"
                        body="The realistic People Engine score: PE Ideal × ROE. This is the headline number to share with your CFO — it answers 'how much workforce capacity are you ACTUALLY delivering against your business goals, after accounting for the gap between what people learned and what they do on the job?' Use this, not PE Ideal, for resource decisions."
                      />
                    </div>
                    <p className="text-[10px] text-purple-500 uppercase tracking-wider mb-1">PE Validated</p>
                    <p className="text-xl font-bold text-purple-700">{roeHealth.pe.validated_pct}%</p>
                    <p className="text-[10px] text-purple-400">PE × ROE</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-rose-50 border border-rose-200 relative">
                    <div className="absolute top-1 right-1">
                      <TileInfoButton
                        testid="pe-roe-impact-gap-info"
                        title="Transfer Gap"
                        body="The slice of theoretical workforce capacity that is NOT making it to the job. Equals (PE Ideal − PE Validated). Every percentage point here is L&D budget that was spent but isn't moving the business — usually the cheapest wins are recovered by fixing manager-coaching and system-of-work blockers, not by buying more content."
                        linkLabel="Open ROE Health"
                        onLink={() => navigate('/roe-health')}
                      />
                    </div>
                    <p className="text-[10px] text-rose-500 uppercase tracking-wider mb-1">Transfer Gap</p>
                    <p className="text-xl font-bold text-rose-500">{roeHealth.pe.transfer_gap_pct}%</p>
                    <p className="text-[10px] text-rose-400">unrealized capacity</p>
                  </div>
                </div>
                {roeHealth.pe.transfer_gap_pct > 5 && (
                  <div className="mt-3 p-2.5 bg-purple-50 border border-purple-200 rounded-lg text-sm text-purple-800">
                    <strong>{Math.round(100 - (roeHealth.roe_actual.pct))}%</strong> of your workforce's theoretical capacity is not translating into actual performance. Focus on the
                    {roeHealth.deficiencies?.length > 0 ? ` ${roeHealth.deficiencies[0].area.toLowerCase()} gap` : ' weakest ROE component'} to close this — view <button onClick={() => navigate('/roe-health')} className="underline font-semibold hover:text-purple-900">Behavior Health</button> for details.
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="border border-dashed border-purple-200" data-testid="pe-roe-empty">
              <CardContent className="p-4 flex items-center gap-4">
                <ClipboardCheck className="h-5 w-5 text-purple-400" />
                <div>
                  <p className="text-sm font-medium text-slate-600">ROE Validation Not Available</p>
                  <p className="text-xs text-slate-400">Import L2 assessment scores, motivation surveys, and L3 manager observations in <button onClick={() => navigate('/roe-health')} className="text-purple-600 underline hover:text-purple-800">Behavior Health</button> to see validated PE.</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Cohort PE Comparison + table now live on the Overview tab. */}
            </>
          ) : (
            <Card>
              <CardContent className="p-12 text-center text-slate-500">
                No People Engine data found. Use the seed endpoint or import data to get started.
              </CardContent>
            </Card>
          )}
          </div>
        </CollapsibleSection>

      {/* PE Alerts Banner */}
      <PEAlertsBanner orgId={selectedOrg} authHeaders={headers()} />

      {/* Priority focus — same coaching surface used on Maturity
          and LEI Health, ported here. Identifies the lowest-
          scoring CMR-LE component as the "weak link" and pairs
          the headline with a hybrid (static + AI) action plan
          card. Skipped while loading or when there's no data so
          the existing empty-state CTAs still drive the user. */}
      {!loading && current && (() => {
        const componentLabels = {
          capability: 'Capability (C)',
          motivation: 'Motivation (M)',
          retention: 'Retention (R)',
          lei: 'Learning Effectiveness (LE)',
        };
        const candidates = [
          ['capability', current.capability],
          ['motivation', current.motivation],
          ['retention', current.retention],
          ['lei', current.lei],
        ].filter(([, v]) => typeof v === 'number' && v > 0);
        if (candidates.length === 0) return null;
        const [weakKeyDefault, weakValDefault] = candidates.reduce(
          (lo, kv) => (kv[1] < lo[1] ? kv : lo),
          candidates[0],
        );
        // Honor a deep-link from the Overview Alerts & Risks list — if a specific
        // component was requested, build the action plan for THAT component.
        const focusKey = location.state?.focusComponent;
        const focusMatch = candidates.find(([k]) => k === focusKey);
        const [weakKey, weakVal] = focusMatch || [weakKeyDefault, weakValDefault];
        // Build the financial-impact story. PE score is 0..1
        // (fraction-realized). Lifting the weakest component
        // by +15pts on the 100-scale moves realized share up
        // by up to 15/total_components ≈ 3.75 percentage points
        // of the composite — BUT the user-facing narrative is
        // on the component's own 0..100 scale (same as LEI),
        // which is what the CFO-facing dollar story actually
        // tracks. $/year leaking = VAS × (1 − realized). $/year
        // unlocked = VAS × (delta/100).
        const LIFT_POINTS = 15;
        const realizedPct = Math.max(0, Math.min(100, weakVal * 100));
        const afterFixPct = Math.min(100, realizedPct + LIFT_POINTS);
        const deltaPct = afterFixPct - realizedPct;
        const vas = valueAtStake?.annual_value_at_stake_usd ?? null;
        const currency = valueAtStake?.currency || 'USD';
        const financialImpact = {
          realizedPct,
          afterFixPct,
          deltaPct,
          currency,
          liftLabel: `Lift ${componentLabels[weakKey]} +${LIFT_POINTS}pts`,
          dollarsLeaking: vas != null ? vas * (1 - realizedPct / 100) : null,
          dollarsUnlocked: vas != null ? vas * (deltaPct / 100) : null,
          leakCopy: `Every quarter you leave ${componentLabels[weakKey]} at ${Math.round(realizedPct)}/100, roughly ${Math.round(100 - realizedPct)}% of every learning dollar leaks before it reaches the business.`,
          fixCopy: `Closing the ${componentLabels[weakKey]} gap by ${LIFT_POINTS}pts shifts your realized L&D ROI by ${Math.round(deltaPct)} percentage points.`,
          vasFootnote: valueAtStake ? {
            revenue: valueAtStake.inputs?.annual_revenue,
            marginPct: valueAtStake.inputs?.operating_margin_pct,
            isDefault: !!valueAtStake.is_default,
          } : null,
        };
        return (
          <section className="mb-6" data-testid="pe-priority-focus-section">
            <div className="mb-3">
              <h2 className={`text-lg font-bold leading-tight ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>Risk Areas &amp; Action Plan</h2>
              <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Biggest risk to close + your recommended action plan</p>
            </div>
            <div className="space-y-3">
            <EngineWeakLinkCallout
              entityLabel={componentLabels[weakKey]}
              score={weakVal * 100}
              overall={current.pe ? current.pe * 100 : undefined}
              metricSuffix="/100"
              description="is your lowest-scoring component right now."
              testIdPrefix="pe-weak-link"
              financialImpact={financialImpact}
              modelFixCta={{
                label: `Model ${componentLabels[weakKey]} +15pt lift in Portfolio Analysis`,
                onClick: () => {
                  // Soft handoff — Portfolio Analysis is
                  // LEI-measure-driven, and PE components are
                  // downstream of LEI pillars. We pass a
                  // focusReason so the page can surface a
                  // contextual banner once we wire it up; for
                  // now the navigation + landing toast land the
                  // CLO where they need to be.
                  navigate('/portfolio', {
                    state: {
                      focusReason: 'pe-weak-link',
                      focusPEComponent: weakKey,
                      focusPEComponentLabel: componentLabels[weakKey],
                      focusPELiftPoints: 15,
                      autoPin: true,
                    },
                  });
                  toast.info(
                    `Adjust the L&D measures most tied to ${componentLabels[weakKey]}, then click Calculate — the scenario will auto-pin to your Quarterly Deck.`,
                  );
                },
              }}
            />
            <EngineActionPlanCard
              engine="pe"
              params={{
                component: weakKey,
                organization_id: selectedOrg,
                cohort_id: selectedCohort !== 'all' ? selectedCohort : undefined,
                score: weakVal * 100,
                overall_score: current.pe ? current.pe * 100 : undefined,
              }}
            />
            </div>
          </section>
        );
      })()}
      </>
      )}
      </div>
    </PageShell>
  );
}
